
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3k7da9d1hbhs86av570', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
              Start Your Project Today
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Ready to discuss your construction, HVAC, or specialty facility project? Our team of experts is here
              to provide custom solutions tailored to your unique requirements.
            </p>

            <div className="space-y-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-phone-line text-orange-500 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Phone</h3>
                  <p className="text-gray-600">1 (425) 699-1038</p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-mail-line text-orange-500 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Email</h3>
                  <p className="text-gray-600">mysgbs@outlook.com</p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-map-pin-line text-orange-500 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Location</h3>
                  <p className="text-gray-600">
                    4920 Evergreen Way #1126, Everett WA 98203
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Why Choose Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  25+ years of industry experience
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  Licensed and fully insured
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  24/7 emergency support
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  Sustainable and energy-efficient solutions
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-slate-800 mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5000 focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-slate-800 mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5000 focus:border-transparent text-sm"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-semibold text-slate-800 mb-2"
                >
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5000 focus:border-transparent text-sm"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label
                  htmlFor="projectType"
                  className="block text-sm font-semibold text-slate-800 mb-2"
                >
                  Project Type *
                </label>
                <div className="relative">
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5000 focus:border-transparent text-sm appearance-none bg-white"
                  >
                    <option value="">Select project type</option>
                    <option value="construction-management">
                      Construction Project Management
                    </option>
                    <option value="hvac-installation">HVAC Installation</option>
                    <option value="specialty-facility">
                      Specialty Facility Project
                    </option>
                    <option value="consultation">Consultation</option>
                    <option value="other">Other</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-slate-800 mb-2"
                >
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5000 focus:border-transparent text-sm resize-vertical"
                  placeholder="Describe your project requirements, timeline, and any specific needs..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-slate-800 hover:bg-slate-700 disabled:bg-gray-400 text-white py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Project Inquiry'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you! Your project inquiry has been sent successfully. We'll
                  contact you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Sorry, there was an error sending your message. Please try again
                  or contact us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
